import { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material';

import CloseIcon from '@mui/icons-material/CloseOutlined';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import SkipNextSharpIcon from '@mui/icons-material/SkipNextSharp';
import SkipPreviousSharpIcon from '@mui/icons-material/SkipPreviousSharp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { toSecond } from './utilities';
import { normalizeTagData } from './common';
import { filterClipIndices, nextVisibleIndex } from './clipFilter';
import { getUser } from '../../../common/utilities';
import { getClipEditMode } from '../../../common/permissions';
import gameService from '../../../services/game.service';
import PlayerTagEditRequestDialog from '../../player/PlayerTagEditRequestDialog';
import GameExportToEdits from '../games/tabs/overview/exportEdits';
import { buildClipName, hasClipNameColumns } from '../games/tabs/overview/tagKeys';
import { TEAM_ICON_DEFAULT } from '../../../common/staticData';

import '../../player/Profile.css';

/**
 * Shared Video Player component used across all pages
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes, receives (hasUpdates: boolean)
 * @param {string|Array} video_url - Single video URL string OR array of { id, video_url } objects
 * @param {Array} tagList - List of tags/clips to display
 * @param {function} t - Translation function
 * @param {boolean} showEditDialog - Caller opt-out for the tag edit dialog (default: true).
 *                                   The pen also requires manager or coach permission.
 */
const SharedVideoPlayer = ({ open, onClose, video_url, tagList, t, showEditDialog = true, tagName = '' }) => {
    const player = useRef(null);
    const [playRate, setPlayRate] = useState(1);
    const [play, setPlay] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoURL, setVideoURL] = useState('');
    const [videoList, setVideoList] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [tagEditOpen, setTagEditOpen] = useState(false);
    const [updated, setUpdated] = useState(0);
    const [videoReady, setVideoReady] = useState(false);
    // Whether the <ReactPlayer> itself has loaded. Distinct from videoReady,
    // which means playback has actually reached the clip and the cover can lift.
    const [playerReady, setPlayerReady] = useState(false);
    const [pendingSeek, setPendingSeek] = useState(null);
    const [checkedClips, setCheckedClips] = useState({});
    const [exportOpen, setExportOpen] = useState(false);
    const [showTimeScore, setShowTimeScore] = useState(true);
    const [search, setSearch] = useState('');

    // Check if video_url is an array (multi-video mode) or string (single-video mode)
    const isMultiVideo = Array.isArray(video_url);

    // 'immediate' for managers, 'request' for coaches, null hides the pen.
    const clipEditMode = getClipEditMode(getUser());
    const canEditClips = showEditDialog && clipEditMode !== null;

    const getPeriodLabel = (item) => {
        if (item.period) {
            const period = item.period.toString();
            if (period === '1' || period === 'H1') return t('First Half');
            if (period === '2' || period === 'H2') return t('Second Half');
            return period;
        }
        return '';
    };

    // Minute of the game the clip starts at. normalizeTagData yields an integer,
    // but raw rows from the unmigrated endpoints carry game_time instead.
    const getTimeLabel = (item) => {
        const parsed = parseInt(item.time_in_game ?? item.game_time, 10);
        return Number.isNaN(parsed) ? '' : `${parsed}'`;
    };

    // Running score when the clip starts. The tag functions spell these both
    // singular and plural, so accept either. Render nothing unless BOTH sides are
    // present - a half-known score reads as wrong data rather than missing data.
    const getScoreLabel = (item) => {
        const home = item.home_team_goals ?? item.home_team_goal;
        const away = item.away_team_goals ?? item.away_team_goal;
        const missing = (v) => v === undefined || v === null || v === '';
        if (missing(home) || missing(away)) return '';
        return `${home} - ${away}`;
    };

    const getClipDescription = (item) => {
        // Clips from the generic /game/tagclips endpoint carry discrete opening and
        // closing columns, so they get the richer label. Everything else keeps the
        // aggregate format below unchanged.
        if (hasClipNameColumns(item)) return buildClipName(item);

        const playerName = item.player_name || item.player_names || item.name || item.full_name || '';
        const actionName = item.action_name || item.action_names || '';
        const actionType = item.action_type || item.action_type_name || item.action_type_names || '';
        const actionResult = item.action_result || item.action_result_name || item.action_result_names || '';

        const parts = [playerName, actionName, actionType, actionResult]
            .map(p => (p || '').toString().trim())
            .filter(p => p && p.length > 0);

        return parts.join(' - ') || 'Video clip';
    };

    // Original tagList indices matching the search box. Selection, the current
    // clip and seeking are all keyed by original index, so filtering must not
    // renumber clips.
    const visibleIndices = useMemo(
        () => filterClipIndices(tagList, search, (item) => `${getPeriodLabel(item)} ${getTimeLabel(item)} ${getScoreLabel(item)} ${getClipDescription(item)}`),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tagList, search, t]
    );
    const isFiltered = search.trim().length > 0;

    const fastVideo = (param) => {
        player.current && player.current.seekTo(currentTime + param);
    };

    const onProgress = (seconds) => {
        if (tagList.length === 0) return;

        setCurrentTime(seconds);

        // Hold the cover until playback has actually reached the clip we seeked to.
        // Lifting it on a timer instead showed whatever frame the video happened to
        // be sitting on - usually the very first frame of the source.
        if (!videoReady) {
            const target = toSecond(tagList[currentIndex]?.start_time);

            if (pendingSeek === null && seconds >= target - 1) {
                setVideoReady(true);
            }

            return;
        }

        // Ignore progress while a seek or a video switch is still in flight. The
        // player keeps reporting the OLD position for a tick or two, and comparing
        // that to the new clip's window advances again immediately - in multi-video
        // mode the next clip can be in a different game whose timeline is unrelated,
        // so a stale position can cascade through several clips in one go.
        if (pendingSeek !== null) return;

        const clip = tagList[currentIndex];

        // Same reason: only judge the playhead against a clip belonging to the
        // video that is actually loaded.
        if (isMultiVideo) {
            const clipVideo = videoList.filter((item) => Number(item.id) === Number(clip.game_id))[0];
            if (!clipVideo || clipVideo.url !== videoURL) return;
        }

        const start = toSecond(clip.start_time);
        const end = toSecond(clip.end_time);

        if (seconds >= end) {
            if (currentIndex < tagList.length - 1) PlayVideo(1);
            else {
                setPlay(false);
            }
        } else if (seconds <= start) {
            if (playRate !== 1) {
                setPlay(false);
                setPlayRate(1);
            }

            player.current.seekTo(start);
            setPlay(true);
        }
    };

    const PlayVideo = async (add) => {
        // Step within the filtered set so hidden clips are skipped.
        const index = nextVisibleIndex(visibleIndices, currentIndex, add);

        if (index === null) {
            if (add > 0) setPlay(false);
            return;
        }

        setCurrentIndex(index);

        if (isMultiVideo) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[index].game_id))[0];
            if (matchedVideo) {
                const isSameVideo = matchedVideo.url === videoURL;
                if (isSameVideo) {
                    player.current.seekTo(toSecond(tagList[index].start_time));
                } else {
                    setPlay(false);
                    setVideoReady(false);
                    setPlayerReady(false);
                    setVideoURL(matchedVideo.url);
                    setPendingSeek(toSecond(tagList[index].start_time));
                }
            }
        } else {
            player.current.seekTo(toSecond(tagList[index].start_time));
        }
    };

    const handleClipClick = (index) => {
        setCurrentIndex(index);

        if (isMultiVideo) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[index].game_id))[0];
            if (matchedVideo) {
                const isSameVideo = matchedVideo.url === videoURL;
                if (isSameVideo) {
                    player.current.seekTo(toSecond(tagList[index].start_time));
                    setPlay(true);
                } else {
                    setPlay(false);
                    setVideoReady(false);
                    setPlayerReady(false);
                    setVideoURL(matchedVideo.url);
                    setPendingSeek(toSecond(tagList[index].start_time));
                }
            }
        } else {
            player.current.seekTo(toSecond(tagList[index].start_time));
            setPlay(true);
        }
    };

    const handleFullscreen = () => {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        }
    };

    const handleVideoReady = () => {
        setPlayerReady(true);

        if (pendingSeek !== null && player.current) {
            player.current.seekTo(pendingSeek);
            setPendingSeek(null);
        }

        // Autoplay: the first clip starts on its own once the player is ready.
        // Unconditional - a cached video fires onReady BEFORE the clip list has
        // resolved a seek target, and gating this on pendingSeek meant the modal
        // opened paused until a clip was clicked. videoReady is still set from
        // onProgress, when playback actually reaches the clip.
        setPlay(true);
    };

    // The seek target can arrive after the player is already loaded, in which
    // order handleVideoReady has nothing to seek to. Seek and start here instead.
    useEffect(() => {
        if (playerReady && pendingSeek !== null && player.current) {
            player.current.seekTo(pendingSeek);
            setPendingSeek(null);
            setPlay(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerReady, pendingSeek]);

    // Handle multi-video mode: build video list
    useEffect(() => {
        if (isMultiVideo) {
            setVideoList([]);
            video_url.forEach((game) => {
                if (game.video_url.includes('youtube.com') || game.video_url.includes('youtu.be')) {
                    gameService.getNewStreamURL(game.video_url).then((res) => {
                        setVideoList((old) => [...old, { id: game.id, url: res.result.video_url }]);
                    });
                } else if (game.video_url.toLowerCase() !== 'no video') {
                    setVideoList((old) => [...old, { id: game.id, url: game.video_url }]);
                }
            });
        }
    }, [video_url, isMultiVideo]);

    // Handle single-video mode: set video URL directly
    useEffect(() => {
        if (!isMultiVideo && video_url) {
            if (video_url.includes('youtube.com') || video_url.includes('youtu.be')) {
                gameService.getNewStreamURL(video_url).then((res) => {
                    setVideoURL(res.url || res.result?.video_url || '');
                });
            } else if (video_url.toLowerCase() !== 'no video') {
                setVideoURL(video_url);
            }
        }
    }, [video_url, isMultiVideo]);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setCurrentIndex(0);
            setVideoReady(false);
            setPlayerReady(false);
            setPlay(false);
            if (isMultiVideo) {
                setVideoURL('');
            }
            setPendingSeek(null);
            setCheckedClips({});
            setSearch('');
        }
    }, [open, isMultiVideo]);

    // Handle initial video load when videoList becomes available (multi-video mode)
    useEffect(() => {
        if (isMultiVideo && open && videoList.length > 0 && tagList.length > 0 && !videoURL) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[0].game_id))[0];
            if (matchedVideo) {
                setVideoURL(matchedVideo.url);
                setPendingSeek(toSecond(tagList[0].start_time));
            }
        }
    }, [open, videoList, tagList, videoURL, isMultiVideo]);

    // For single-video mode, seek to the first clip.
    //
    // No player.current guard: the ref is still null when this first runs and the
    // effect does not re-run when it fills in, so the seek target was never set.
    // The video then played from 0 instead of the clip, and because the cover only
    // lifts once playback REACHES the clip, the modal looked frozen on the first
    // frame. The playerReady effect performs the seek once the player exists.
    useEffect(() => {
        if (!isMultiVideo && open && videoURL && tagList.length > 0) {
            setPendingSeek(toSecond(tagList[0].start_time));
        }
    }, [open, videoURL, tagList, isMultiVideo]);

    // Checkbox handlers
    const handleCheckClip = (index) => (e) => {
        e.stopPropagation();
        setCheckedClips(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleSelectAll = () => {
        // Acts on the clips currently visible; selections made under a
        // different search stay put rather than being silently discarded.
        const allChecked = visibleIndices.length > 0 && visibleIndices.every((index) => checkedClips[index]);

        setCheckedClips((prev) => {
            const next = { ...prev };

            visibleIndices.forEach((index) => {
                if (allChecked) delete next[index];
                else next[index] = true;
            });

            return next;
        });
    };

    const getSelectedClips = () => {
        const selected = tagList.filter((_, index) => checkedClips[index]);
        return normalizeTagData(selected);
    };

    const selectedCount = Object.values(checkedClips).filter(Boolean).length;
    const visibleSelectedCount = visibleIndices.filter((index) => checkedClips[index]).length;
    const allSelected = visibleIndices.length > 0 && visibleSelectedCount === visibleIndices.length;

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={() => onClose(updated > 0)}
            PaperProps={{
                style: { backgroundColor: '#1a1b1d' }
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', height: '100vh', position: 'relative' }}>
                {/* Time/Score Overlay - fixed position over everything */}
                {showTimeScore && tagList.length > 0 && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 20,
                            left: '25%',
                            transform: 'translateX(-50%)',
                            zIndex: 99999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            pointerEvents: 'none'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#17375E', px: 2, py: 0.5, borderRadius: '5px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.2rem', fontWeight: 500, color: '#ffffff' }}>
                                {`${tagList[currentIndex]?.period === 1 ? 'H1' : tagList[currentIndex]?.period === 2 ? 'H2' : 'OT'} - ${tagList[currentIndex]?.time_in_game || 0}'`}
                            </Typography>
                        </Box>
                        <img src={tagList[currentIndex]?.home_team_logo ? (tagList[currentIndex].home_team_logo.includes(process.env.REACT_APP_S3_URI) ? tagList[currentIndex].home_team_logo : process.env.REACT_APP_S3_URI + tagList[currentIndex].home_team_logo) : TEAM_ICON_DEFAULT} style={{ width: '40px', height: '40px', objectFit: 'contain', background: '#fff', borderRadius: '4px' }} alt="" />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', px: 2, py: 0.5, borderRadius: '5px', minWidth: '80px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.2rem', fontWeight: 500, color: '#17375E' }}>
                                {`${tagList[currentIndex]?.home_team_goals ?? '-'} : ${tagList[currentIndex]?.away_team_goals ?? '-'}`}
                            </Typography>
                        </Box>
                        <img src={tagList[currentIndex]?.away_team_logo ? (tagList[currentIndex].away_team_logo.includes(process.env.REACT_APP_S3_URI) ? tagList[currentIndex].away_team_logo : process.env.REACT_APP_S3_URI + tagList[currentIndex].away_team_logo) : TEAM_ICON_DEFAULT} style={{ width: '40px', height: '40px', objectFit: 'contain', background: '#fff', borderRadius: '4px' }} alt="" />
                    </Box>
                )}
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
                    {/* Video Section - Left */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#000', position: 'relative' }}>
                        <Box sx={{ flex: 1, position: 'relative' }}>
                            {/* Covers the player until playback reaches the clip, so no
                                frame of the source is shown while it loads and seeks.
                                Applies to single-video mode and to video switches too. */}
                            {!videoReady && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: '#000',
                                        zIndex: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress sx={{ color: '#fff' }} />
                                </Box>
                            )}
                            <ReactPlayer
                                url={videoURL}
                                ref={player}
                                onReady={handleVideoReady}
                                onPlay={() => setPlay(true)}
                                onPause={() => setPlay(false)}
                                onProgress={(p) => onProgress(p.playedSeconds)}
                                playing={play}
                                controls={false}
                                playbackRate={playRate}
                                width="100%"
                                height="100%"
                                style={{ position: 'absolute', top: 0, left: 0 }}
                            />
                        </Box>

                        {/* Video Controls */}
                        <Box sx={{ backgroundColor: '#1a1b1d', p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <IconButton onClick={() => PlayVideo(-1)} sx={{ color: '#fff' }}>
                                    <SkipPreviousSharpIcon />
                                </IconButton>
                                <IconButton onClick={() => fastVideo(-3)} sx={{ color: '#fff' }}>
                                    <FastRewindIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        if (playRate === 1) setPlay((p) => !p);
                                        else setPlayRate(1);
                                    }}
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: '#333',
                                        '&:hover': { backgroundColor: '#444' },
                                        width: 48,
                                        height: 48
                                    }}
                                >
                                    {play && playRate === 1 ? <PauseIcon /> : <PlayIcon />}
                                </IconButton>
                                <IconButton onClick={() => fastVideo(3)} sx={{ color: '#fff' }}>
                                    <FastForwardIcon />
                                </IconButton>
                                <IconButton onClick={() => PlayVideo(1)} sx={{ color: '#fff' }}>
                                    <SkipNextSharpIcon />
                                </IconButton>
                                <FormControlLabel
                                    control={<Switch checked={showTimeScore} onChange={(e) => setShowTimeScore(e.target.checked)} size="small" />}
                                    label="T/S"
                                    sx={{ color: '#fff', ml: 2, '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                                />
                                <IconButton onClick={handleFullscreen} sx={{ color: '#fff' }}>
                                    <FullscreenIcon />
                                </IconButton>
                                {canEditClips && tagList.length > 0 && (
                                    <Tooltip title={t('Request Correction')}>
                                        <IconButton
                                            onClick={() => {
                                                setPlay(false);
                                                setTagEditOpen(true);
                                            }}
                                            sx={{ color: '#fff' }}
                                        >
                                            <EditOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Clips List - Right */}
                    <Box sx={{ width: '400px', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                        {/* Header with close button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: '1px solid #e0e0e0' }}>
                            {/* Which button these clips came from, then how many.
                                Long labels ellipsis rather than pushing the count
                                or the close button out of the panel. */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', pl: 1, minWidth: 0, flex: 1 }}>
                                {tagName && (
                                    <Typography
                                        title={t(tagName)}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            color: '#17375E',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {t(tagName)}
                                    </Typography>
                                )}
                                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                    {t('Clips')} ({isFiltered ? `${visibleIndices.length}/${tagList.length}` : tagList.length})
                                </Typography>
                            </Box>
                            <IconButton onClick={() => onClose(updated > 0)} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Search / filter clips */}
                        <Box sx={{ px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('Search clips')}
                                inputProps={{ 'aria-label': t('Search clips') }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" aria-label={t('Clear search')} onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                                                <CloseIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', backgroundColor: '#f9fafb' } }}
                            />
                        </Box>

                        {/* Select All and Export controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={visibleSelectedCount > 0 && !allSelected}
                                    onChange={handleSelectAll}
                                    sx={{ p: 0.5 }}
                                />
                                <Typography sx={{ fontSize: '12px', color: '#6b7280', ml: 0.5 }}>
                                    {selectedCount > 0 ? `${selectedCount} ${t('selected')}` : t('Select All')}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                                disabled={selectedCount === 0}
                                onClick={() => setExportOpen(true)}
                                sx={{
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    py: 0.25,
                                    px: 1,
                                    borderColor: '#e5e7eb',
                                    color: selectedCount > 0 ? '#15803d' : '#9ca3af',
                                    '&:hover': {
                                        borderColor: '#15803d',
                                        backgroundColor: '#f0fdf4'
                                    }
                                }}
                            >
                                {t('Export to Edits')}
                            </Button>
                        </Box>

                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {isFiltered && visibleIndices.length === 0 && (
                                <Typography sx={{ p: 2, fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                                    {t('No clips match your search')}
                                </Typography>
                            )}
                            {visibleIndices.map((index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        p: 1.5,
                                        cursor: 'pointer',
                                        backgroundColor: currentIndex === index ? '#C5102C' : 'transparent',
                                        color: currentIndex === index ? '#fff' : '#1a1b1d',
                                        '&:hover': {
                                            backgroundColor: currentIndex === index ? '#C5102C' : '#f5f5f5'
                                        },
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={!!checkedClips[index]}
                                        onChange={handleCheckClip(index)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            p: 0.5,
                                            mr: 1,
                                            color: currentIndex === index ? '#fff' : undefined,
                                            '&.Mui-checked': {
                                                color: currentIndex === index ? '#fff' : '#6366f1'
                                            }
                                        }}
                                    />
                                    <Box onClick={() => handleClipClick(index)} sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 600, minWidth: '20px', fontSize: '13px', color: 'inherit' }}>
                                                {index + 1}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'inherit' }}>
                                                {getPeriodLabel(tagList[index])}
                                            </Typography>
                                            {getTimeLabel(tagList[index]) && (
                                                <Typography sx={{ fontWeight: 500, fontSize: '12px', color: 'inherit', opacity: 0.85 }}>
                                                    {getTimeLabel(tagList[index])}
                                                </Typography>
                                            )}
                                            {getScoreLabel(tagList[index]) && (
                                                <Typography sx={{ fontWeight: 600, fontSize: '12px', color: 'inherit', opacity: 0.85 }}>
                                                    {getScoreLabel(tagList[index])}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: '12px', opacity: 0.9, color: 'inherit', mt: 0.5 }}>
                                            {getClipDescription(tagList[index])}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {canEditClips && tagList.length > 0 && (
                    <PlayerTagEditRequestDialog
                        t={t}
                        open={tagEditOpen}
                        onClose={(flag) => {
                            setTagEditOpen(false);
                            setPlay(true);
                            if (flag) setUpdated((c) => c + 1);
                        }}
                        player={tagList[currentIndex]}
                        cur_player_id={tagList[currentIndex]?.player_id}
                        applyImmediately={clipEditMode === 'immediate'}
                    />
                )}

                {/* Export to My Edits Dialog */}
                <GameExportToEdits
                    t={t}
                    open={exportOpen}
                    onClose={() => setExportOpen(false)}
                    tagList={getSelectedClips()}
                    isTeams={true}
                />
            </DialogContent>
        </Dialog>
    );
};

export default SharedVideoPlayer;
